import type { Bay, Locale } from '@kilat/shared';

/**
 * Pure bay-name resolution shared by the queue board's picker + card chip.
 *
 * Bays live at `tenants/{tid}/outlets/{oid}/bays/{bayId}` and carry an
 * `I18nString` name (see `BaySchema`). Bay names are stored identically
 * across every locale (the config UI writes the same string to en/ms/zh/ta),
 * so resolution is a simple locale-with-en-fallback lookup — kept here as a
 * pure function so the board's display path is unit-testable without React.
 */

/** Display name for a single bay in `locale`, falling back to `en`. */
export function bayDisplayName(bay: Bay, locale: Locale): string {
  return bay.name[locale] ?? bay.name.en;
}

/**
 * Resolve a `ticket.bayId` to a display name for the current `locale`.
 *
 * Returns `null` when there is no bay id, or when the id no longer resolves to
 * a bay in `bays` (e.g. a bay that was hard-deleted out of band). A bay that
 * was merely deactivated or renamed still resolves — the config UI soft-deletes
 * (keeps `active:false`) precisely so historical `ticket.bayId` refs stay
 * resolvable and the board shows the bay's current stored name gracefully.
 */
export function resolveBayName(
  bayId: string | undefined,
  bays: readonly Bay[],
  locale: Locale,
): string | null {
  if (!bayId) return null;
  const bay = bays.find((b) => b.id === bayId);
  return bay ? bayDisplayName(bay, locale) : null;
}
